import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraOverlayDrawer, SpecraSelect } from '@mono/ui-components';
import { SpecraFileList } from '@mono/ui-components/captains';
import { SpecraBreadcrumbs, SpecraEmptyState, SpecraFormField, SpecraInlineBanner, SpecraSearchField, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { SharePointLinkMode } from '../../../../../../../gen/ts/mono/projectdata/v1/projectdata_pb';
import { useAuth } from '../../../../contexts/authContext';
import { useSharePoint } from '../../../../hooks/useSharePoint';
import { DOCUMENT_CATEGORIES, SENSITIVITIES } from './categories';
import { documentErrorText } from './useProposalDocuments';
const T = Testids.proposalDocuments;
export function LinkFromSharePointDrawer(p) {
    const { user } = useAuth();
    const sp = useSharePoint();
    const [site, setSite] = useState(null);
    const [drive, setDrive] = useState(null);
    const [folders, setFolders] = useState([]);
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [pages, setPages] = useState([
        ''
    ]);
    const [mode, setMode] = useState('link');
    const [category, setCategory] = useState('reference');
    const [sensitivity, setSensitivity] = useState('');
    const mutation = p.writes.sharepoint;
    const busy = mutation.isPending;
    const page = pages[pages.length - 1];
    const folder = folders[folders.length - 1];
    useEffect(()=>{
        if (search.trim() === query) return;
        const timer = setTimeout(()=>{
            setQuery(search.trim());
            setPages([
                ''
            ]);
            setSelected(null);
        }, 250);
        return ()=>clearTimeout(timer);
    }, [
        search,
        query
    ]);
    const browser = useQuery({
        queryKey: [
            'proposal-sharepoint-browser',
            user?.tenant_id,
            user?.id,
            site?.id,
            drive?.id,
            folder?.id,
            query,
            page
        ],
        retry: false,
        gcTime: 0,
        queryFn: async ()=>{
            if (query) {
                const result = await sp.searchDocuments(query, 50, Number(page || 0));
                if (!result) throw new Error('SharePoint search could not be loaded.');
                return {
                    entries: result.hits.map((h)=>({
                            id: h.item_id,
                            name: h.name,
                            driveId: h.drive_id,
                            kind: 'file'
                        })),
                    next: result.more_results_available ? String(Number(page || 0) + 50) : ''
                };
            }
            if (!site) {
                const result = await sp.getUserSiteAccess(user?.id ?? '', user?.tenant_id ?? '');
                if (!result) throw new Error('SharePoint sites could not be loaded.');
                return {
                    entries: result.sites.map((s)=>({
                            id: s.site_id,
                            name: s.site_name,
                            kind: 'site'
                        })),
                    next: ''
                };
            }
            if (!drive) {
                const result = await sp.listSiteDrives(site.id);
                if (!result) throw new Error('SharePoint drives could not be loaded.');
                return {
                    entries: result.filter((d)=>d.drive_type !== 'personal').map((d)=>({
                            id: d.id,
                            name: d.name,
                            kind: 'drive'
                        })),
                    next: ''
                };
            }
            const result = await sp.listDriveItems(site.id, drive.id, folder?.id, 50, page);
            if (!result) throw new Error('SharePoint documents could not be loaded.');
            return {
                entries: (result.items ?? []).map((i)=>({
                        id: i.id,
                        name: i.name,
                        driveId: drive.id,
                        kind: i.is_folder ? 'folder' : 'file'
                    })),
                next: result.next_page_token ?? ''
            };
        }
    });
    const resetSelection = ()=>{
        setSelected(null);
        setPages([
            ''
        ]);
    };
    const navigate = (entry)=>{
        if (busy) return;
        if (entry.kind === 'file') {
            setSelected(entry);
            return;
        }
        resetSelection();
        if (entry.kind === 'site') setSite(entry);
        else if (entry.kind === 'drive') setDrive(entry);
        else if (entry.kind === 'folder') setFolders([
            ...folders,
            entry
        ]);
    };
    const close = ()=>{
        if (!busy) p.onClose();
    };
    const submit = ()=>{
        if (!selected?.driveId || !selected.id || busy || !browser.isSuccess || browser.isFetching || search.trim() !== query) return;
        mutation.mutate({
            driveId: selected.driveId,
            itemId: selected.id,
            mode: mode === 'copy' ? SharePointLinkMode.COPY : SharePointLinkMode.LINK,
            hubCategory: category,
            sensitivity
        }, {
            onSuccess: p.onClose
        });
    };
    const crumbs = [
        {
            id: 'sites',
            label: 'SharePoint',
            onClick: ()=>{
                setSite(null);
                setDrive(null);
                setFolders([]);
            }
        },
        ...site ? [
            {
                id: `site:${site.id}`,
                label: site.name,
                onClick: ()=>{
                    setDrive(null);
                    setFolders([]);
                }
            }
        ] : [],
        ...drive ? [
            {
                id: `drive:${drive.id}`,
                label: drive.name,
                onClick: ()=>setFolders([])
            }
        ] : [],
        ...folders.map((f, i)=>({
                id: `folder:${f.id}`,
                label: f.name,
                onClick: ()=>setFolders(folders.slice(0, i + 1))
            }))
    ].map((c, i, all)=>({
            ...c,
            current: i === all.length - 1 && !query,
            testId: T.sharepointCrumb,
            onClick: busy ? undefined : ()=>{
                c.onClick();
                setSearch('');
                setQuery('');
                resetSelection();
            }
        }));
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: true,
        title: "Link from SharePoint",
        description: "Choose a file to link or copy into this proposal.",
        testId: T.sharepointDrawer,
        onOpenChange: (open)=>!open && close(),
        footerProps: {
            confirmLabel: mode === 'copy' ? 'Copy file' : 'Link file',
            onConfirm: submit,
            confirmDisabled: !selected || !browser.isSuccess || browser.isFetching || search.trim() !== query,
            loading: busy,
            onCancel: close,
            cancelProps: {
                disabled: busy
            },
            confirmProps: {
                'data-testid': T.sharepointConfirm
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                mutation.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    role: "alert",
                    "data-testid": T.actionError,
                    children: documentErrorText(mutation.error)
                }) : null,
                /*#__PURE__*/ _jsx(SpecraSearchField, {
                    "aria-label": "Search SharePoint",
                    placeholder: "Search SharePoint",
                    "data-testid": T.sharepointSearch,
                    value: search,
                    disabled: busy,
                    onChange: (e)=>setSearch(e.target.value),
                    onClear: ()=>{
                        if (!busy) setSearch('');
                    }
                }),
                /*#__PURE__*/ _jsx(SpecraBreadcrumbs, {
                    label: "SharePoint path",
                    items: crumbs
                }),
                browser.isPending ? /*#__PURE__*/ _jsx("p", {
                    "aria-busy": "true",
                    "data-testid": T.sharepointLoading,
                    children: "Loading SharePoint…"
                }) : browser.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    role: "alert",
                    "data-testid": T.sharepointError,
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>browser.refetch(),
                        "data-testid": T.sharepointRetry,
                        children: "Try again"
                    }),
                    children: sp.error || documentErrorText(browser.error)
                }) : browser.data.entries.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    "data-testid": T.sharepointEmpty,
                    title: query ? 'No documents match' : 'Nothing here yet',
                    description: "Try another search or choose another location."
                }) : /*#__PURE__*/ _jsx(SpecraFileList, {
                    files: browser.data.entries.map((e)=>({
                            id: `${e.driveId ?? ''}:${e.id}`,
                            name: e.name,
                            category: e.kind === 'file' ? undefined : e.kind,
                            actions: /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                "data-testid": T.sharepointItem,
                                "data-item-id": e.id,
                                "aria-label": `${e.kind === 'file' ? 'Select' : 'Open'} ${e.name}`,
                                "aria-pressed": e.kind === 'file' ? selected?.id === e.id && selected?.driveId === e.driveId : undefined,
                                disabled: busy,
                                onClick: ()=>navigate(e),
                                children: e.kind === 'file' ? 'Select' : 'Open'
                            })
                        }))
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex gap-2",
                    children: [
                        pages.length > 1 ? /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: busy,
                            onClick: ()=>{
                                setPages(pages.slice(0, -1));
                                setSelected(null);
                            },
                            children: "Previous results"
                        }) : null,
                        browser.data?.next ? /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.sharepointNext,
                            disabled: busy || browser.isFetching,
                            onClick: ()=>{
                                setPages([
                                    ...pages,
                                    browser.data.next
                                ]);
                                setSelected(null);
                            },
                            children: "Next results"
                        }) : null
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "File as",
                    testId: T.sharepointMode,
                    value: mode,
                    onValueChange: setMode,
                    options: [
                        {
                            value: 'link',
                            label: 'Link',
                            disabled: busy
                        },
                        {
                            value: 'copy',
                            label: 'Copy',
                            disabled: busy
                        }
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "info",
                    children: mode === 'link' ? 'The file stays in SharePoint. Each viewer needs their own SharePoint access.' : 'A separate copy will be stored in this proposal. Changes in SharePoint will not update it.'
                }),
                selected ? /*#__PURE__*/ _jsxs("output", {
                    children: [
                        "Selected: ",
                        selected.name
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Category",
                    htmlFor: "pd-sharepoint-category",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "pd-sharepoint-category",
                        "data-testid": T.categorySelect,
                        value: category,
                        disabled: busy,
                        onChange: (e)=>setCategory(e.target.value),
                        children: DOCUMENT_CATEGORIES.map((c)=>/*#__PURE__*/ _jsx("option", {
                                value: c.value,
                                children: c.label
                            }, c.value))
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Who can see it",
                    htmlFor: "pd-sharepoint-sensitivity",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "pd-sharepoint-sensitivity",
                        "data-testid": T.sensitivitySelect,
                        value: sensitivity,
                        disabled: busy,
                        onChange: (e)=>setSensitivity(e.target.value),
                        children: SENSITIVITIES.map((s)=>/*#__PURE__*/ _jsx("option", {
                                value: s.value,
                                children: s.label
                            }, s.value))
                    })
                })
            ]
        })
    });
}
